/* eslint-disable consistent-return */
import React, { useEffect } from 'react';
import classNames from 'classnames';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import { useTranslation } from 'react-i18next';
import { Status } from 'src/utils/general';
import Input from 'src/components/ui/Input/Input';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import ProductImage from 'src/components/ui/ProductImage/ProductImage';
import NoResultsFound from 'src/components/ui/NoResultsFound/NoResultsFound';
import { SaleOrderLine, SaleOrderLineLot } from 'src/services/saleOrder';
import { useProducts } from 'src/services/productsNew';
import { useLot } from 'src/services/lots';
import styles from './ProductTable.module.scss';
import { LotCell } from '../../../../pages/Inbound/InboundPackageTable';

export interface ProductTableProps {
  orderLineData: SaleOrderLine[];
  editable?: boolean;
  onChange?: (orderLines: any) => void;
  onOrderLineDelete?: (orderLineId: number) => void;
  onAddProduct?: () => void;
  displayOptions?: { status: boolean };
  onProductClick?: (productId: number) => void;
}

export function ProductStatusBadge({ status }: { status: any }) {
  const badgeType: Record<string, BadgeType> = {
    entwurf: BadgeType.Neutral2,
  };

  return (
    <Badge
      className="max-w-min normal-case"
      label={status.label}
      expanded
      colorOverride={status.color ? status.color : ''}
      type={status.color ? '' : badgeType[status.label.toLowerCase()] ?? BadgeType.Neutral}
    />
  );
}

const LotDisplayCell: React.FC<{
  lotId: number;
  productId: number;
}> = ({ lotId, productId }) => {
  const { lots } = useLot(productId && productId > 0 ? productId : 0);
  const lotName = lots?.find(lot => lot.id === lotId)?.name;

  return <span>{lotName || `Lot ${lotId}`}</span>;
};

const ProductTable: React.FC<ProductTableProps> = ({
  orderLineData,
  editable = false,
  displayOptions = { status: true },
  onChange,
  onOrderLineDelete,
  onAddProduct,
  onProductClick,
}) => {
  const { t } = useTranslation();
  const [orderLines, setOrderLines] = React.useState(orderLineData || []);
  const isUserUpdating = React.useRef(false);

  useEffect(() => {
    if (isUserUpdating.current) {
      return;
    }

    if (orderLineData && Array.isArray(orderLineData)) {
      const normalizedOrderLines = orderLineData.map(orderLine => ({
        ...orderLine,
        discount: orderLine.discount || 0,
        unit_price: orderLine.unit_price || 0,
        applied_quality_checks: orderLine.applied_quality_checks || [],
      }));
      setOrderLines(normalizedOrderLines);
    } else {
      setOrderLines([]);
    }
  }, [orderLineData]);

  /**
   * Update quantity of product in orderLines
   * @param quantity - new quantity
   * @param index - index of product in orderLines
   */
  const updateQuantity = (quantity: string, index: number) => {
    isUserUpdating.current = true;

    const newOrderLines = [...orderLines];
    const parsedQuantity = parseInt(quantity, 10);
    const currentOrderLine = newOrderLines[index];

    newOrderLines[index] = {
      ...currentOrderLine,
      quantity: parsedQuantity,
      discount: currentOrderLine.discount || 0,
      unit_price: currentOrderLine.unit_price || 0,
      applied_quality_checks: currentOrderLine.applied_quality_checks || [],
    };

    const invalid = Number.isNaN(parsedQuantity) || parsedQuantity < 1;

    if (onChange && !invalid) {
      onChange(newOrderLines);
    }
    setOrderLines(newOrderLines);

    setTimeout(() => {
      isUserUpdating.current = false;
    }, 100);
  };

  const updateLot = (lotId: number | null, index: number) => {
    if (index < 0 || index >= orderLines.length) {
      return;
    }

    isUserUpdating.current = true;

    const newOrderLines = [...orderLines];
    const currentOrderLine = newOrderLines[index];

    if (!currentOrderLine) {
      isUserUpdating.current = false;
      return;
    }

    newOrderLines[index] = {
      ...currentOrderLine,
      lot: lotId ? ({ id: lotId } as SaleOrderLineLot) : undefined,
      discount: currentOrderLine.discount || 0,
      unit_price: currentOrderLine.unit_price || 0,
      applied_quality_checks: currentOrderLine.applied_quality_checks || [],
    };

    if (onChange) {
      onChange(newOrderLines);
    }
    setOrderLines(newOrderLines);

    setTimeout(() => {
      isUserUpdating.current = false;
    }, 100);
  };

  /**
   * Delete product from orderLines
   * @param id - orderLineId
   */
  const deleteProduct = (id: number) => {
    if (onOrderLineDelete) {
      onOrderLineDelete(id);
      // eslint-disable-next-line array-callback-return
      const newOrderLines = orderLines?.filter(orderLine => {
        if (orderLine && orderLine.id) {
          return +orderLine.id !== id;
        }
      });
      setOrderLines(newOrderLines);
    }
  };

  const addNewProduct = () => {
    if (onAddProduct) onAddProduct();
  };

  const hasItemWithDraftStatus =
    orderLines && orderLines.some(orderLine => orderLine.product_status?.label === Status.DRAFT);

  const handleRowClick = (event: React.MouseEvent, productId: number) => {
    const target = event.target as HTMLElement;

    if (target.closest('button') || target.closest('input')) return;

    if (onProductClick) {
      onProductClick(productId);
    }
  };

  const uniqueProductIds = Array.from(
    new Set(orderLines?.map(orderLine => orderLine.product?.id).filter(x => x !== undefined && x !== null)),
  );
  const products = useProducts(uniqueProductIds)
    .map(product => product.data)
    .map(r => r?.data);

  return (
    <>
      {!editable && (!orderLines || orderLines.length === 0) && (
        <NoResultsFound smallTitle title={t('general.no_products_found')} />
      )}
      {orderLines && orderLines.length > 0 && (
        <div className={classNames(styles.root, 'mb-16')}>
          <table>
            <thead>
              <tr>
                <th className="typo-text-12 text-faded">{t('general.name')}</th>
                <th className="typo-text-12 text-faded">{t('general.amount')}</th>
                {displayOptions?.status && !editable && (
                  <th className="typo-text-12 text-faded">{t('general.status')}</th>
                )}
                <th className="typo-text-12 text-faded">{t('inbound.lot')}</th>
                {editable && hasItemWithDraftStatus && (
                  <th className="typo-text-12 text-faded">{t('general.remove')}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {orderLines &&
                orderLines.map((orderLine, index) => {
                  const product = products.find(p => p?.id === orderLine.product?.id);
                  return (
                    <tr
                      key={orderLine.id ?? `new-${orderLine.product?.id}-${index}`}
                      className={`${styles.product} ${!editable ? 'cursor-pointer hover:surface-elevated-more' : ''}`}
                      onClick={!editable ? e => handleRowClick(e, orderLine.product?.id as number) : undefined}>
                      <td className="flex items-center gap-x-8 md:gap-x-12">
                        {orderLine.product && (
                          <>
                            <div className="hidden md:block">
                              <ProductImage id={orderLine.product.id as number} />
                            </div>
                            <div>
                              <h4 className="typo-text-14--bold text-full">{orderLine.product.name}</h4>
                              <div className="flex flex-col typo-text-12 text-faded">
                                <span>
                                  {t('general.article_number_short')}: {orderLine.product.article_number}
                                </span>
                                <span className="">
                                  {t('general.barcode')}: {orderLine.product.barcode}
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                      </td>
                      <td>
                        {!editable && (
                          <span className="w-full typo-text-14 inline-table text-center">{orderLine.quantity}</span>
                        )}
                        {editable && (
                          <Input
                            placeholder={String(orderLine?.quantity)}
                            type="number"
                            min={1}
                            value={orderLine.quantity}
                            onChange={e => updateQuantity(e.target.value, index)}
                            error={Number.isNaN(orderLine.quantity) || Number(orderLine?.quantity) < 1}
                            errorMessage={t('general.invalid_quantity')}
                          />
                        )}
                      </td>
                      {displayOptions.status && !editable && (
                        <td>{orderLine.product_status && <ProductStatusBadge status={orderLine.product_status} />}</td>
                      )}
                      <td aria-label="Lot">
                        {!editable ? (
                          <span className="typo-text-14 text-faded">
                            {orderLine.lot?.id ? (
                              <LotDisplayCell lotId={orderLine.lot.id} productId={orderLine.product?.id as number} />
                            ) : (
                              t('inbound.no_lot_selected')
                            )}
                          </span>
                        ) : (
                          <LotCell
                            product={product}
                            allowCreate={false}
                            lotId={orderLine.lot?.id ?? null}
                            productId={orderLine.product?.id as number}
                            setLotId={lotId => {
                              updateLot(lotId, index);
                            }}
                          />
                        )}
                      </td>
                      {editable && (
                        <td aria-label="Delete">
                          <Button
                            tone={ButtonToneProps.ERROR}
                            icon="delete"
                            onClick={() => deleteProduct(Number(orderLine.id))}
                            disabled={orderLines.length < 2}
                            hoverText={orderLines.length < 2 ? t('outbound.delete_product_tooltip') : null}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
      {editable && (
        <Button tone={ButtonToneProps.SECONDARY} icon="plus" title={t('general.add_product')} onClick={addNewProduct} />
      )}
    </>
  );
};

export default ProductTable;
